// Tour steps configuration for different user roles

export const ADMIN_TOUR_STEPS = [
  {
    target: 'body',
    content: '🎉 Welcome to Edifying Teachers! Let\'s take a quick tour of the admin dashboard and its powerful features to help you manage the mentorship platform effectively.',
    placement: 'center',
    disableBeacon: true,
  },
  {
    target: '[data-tour="sidebar-dashboard"]',
    content: '📊 Your Dashboard is your command center. View comprehensive analytics including total users by role, active pairings, meeting completion rates, survey statistics, and overall platform engagement trends.',
    placement: 'right',
    disableBeacon: true,
    offset: 12,
  },
  {
    target: '[data-tour="sidebar-schools"]',
    content: '🏫 Add / Change Partnership Site - Manage all partner schools and districts. Add new schools, view their statistics, assign site leaders, and monitor site-specific activities and engagement.',
    placement: 'right',
    disableBeacon: true,
    offset: 12,
  },
  {
    target: '[data-tour="sidebar-users"]',
    content: '👥 Add / Change User Profiles - Manage all users across the platform. Create new accounts, edit profiles, approve registrations, update roles (Teacher, Edifier, Principal, Site Leader), and deactivate accounts when needed.',
    placement: 'right',
    disableBeacon: true,
    offset: 12,
  },
  {
    target: '[data-tour="sidebar-principals"]',
    content: '👔 Manage Principals - Specifically manage Principal accounts. Assign principals to schools, update their permissions, and oversee their access to school data and reports.',
    placement: 'right',
    disableBeacon: true,
    offset: 12,
  },
  {
    target: '[data-tour="sidebar-credentials"]',
    content: '🔐 Manage Credentials - Control user roles and permissions. Change user roles, update access levels, manage multi-role assignments (like Site Leaders), and ensure proper security access across the platform.',
    placement: 'right',
    disableBeacon: true,
    offset: 12,
  },
  {
    target: '[data-tour="sidebar-pairing"]',
    content: '🤝 Pairing Management - Create and oversee all mentorship pairings. Match teachers with edifiers based on specialties and compatibility, monitor pairing health, track session frequency, and end pairings when complete.',
    placement: 'right',
    disableBeacon: true,
    offset: 12,
  },
  {
    target: '[data-tour="sidebar-requests"]',
    content: '📬 Pairing/Update Requests - Review and approve teacher requests to be paired with specific edifiers. Evaluate compatibility, consider workload, approve, deny, or suggest alternative pairings.',
    placement: 'right',
    disableBeacon: true,
    offset: 12,
  },
  {
    target: '[data-tour="sidebar-notifications"]',
    content: '📢 Send Notifications - Broadcast announcements and updates to users. Send platform-wide notifications, target specific roles (all teachers, all edifiers), schools, or create custom recipient lists.',
    placement: 'right',
    disableBeacon: true,
    offset: 12,
  },
  {
    target: '[data-tour="sidebar-quotes"]',
    content: '💭 Quote Management - Manage daily inspirational quotes shown to users. Add new quotes, edit existing ones, delete outdated quotes, and curate motivational content for the platform.',
    placement: 'right',
    disableBeacon: true,
    offset: 12,
  },
  {
    target: '[data-tour="header-notifications"]',
    content: '🔔 Notification Center - View all your notifications here. Meeting reminders, pairing requests, user registrations, and system alerts will appear here for quick access.',
    placement: 'bottom',
    disableBeacon: true,
    offset: 12,
  },
  {
    target: '[data-tour="header-help"]',
    content: '❓ Help Center - Access the complete help center, browse FAQs, read detailed articles about platform features, and contact support if you need assistance.',
    placement: 'bottom',
    disableBeacon: true,
    offset: 12,
  },
  {
    target: '[data-tour="header-profile"]',
    content: '👤 Your Profile - Click here to access your profile settings, update your information, change your password, and logout when needed.',
    placement: 'bottom',
    disableBeacon: true,
    offset: 12,
  },
  {
    target: 'body',
    content: '✅ Perfect! You\'re now ready to manage the Edifying Teachers platform. You can download detailed reports, monitor program effectiveness, and support your schools\' mentorship initiatives. You can restart this tour anytime from the Help menu.',
    placement: 'center',
  },
];

export const SITE_LEADER_TOUR_STEPS = [
  {
    target: 'body',
    content: '🎉 Welcome to your Site Leader Dashboard! Let\'s explore the tools available to manage your school or district\'s mentorship program.',
    placement: 'center',
    disableBeacon: true,
  },
  {
    target: '[data-tour="sidebar-dashboard"]',
    content: '📊 Your Dashboard provides school-specific analytics. View your site\'s active pairings, meeting statistics, user counts, session completion rates, and overall engagement metrics for your school or district.',
    placement: 'right',
    disableBeacon: true,
    offset: 12,
  },
  {
    target: '[data-tour="sidebar-users"]',
    content: '👥 Manage Users - View and manage all users affiliated with your school or district. See teachers, edifiers, and principals. Update profiles, manage availability status, and coordinate with your team.',
    placement: 'right',
    disableBeacon: true,
    offset: 12,
  },
  {
    target: '[data-tour="sidebar-pairing"]',
    content: '🤝 Pairing Management - Monitor and manage mentorship pairings within your school. View active pairs, check meeting frequency, track progress, and ensure successful mentorship relationships.',
    placement: 'right',
    disableBeacon: true,
    offset: 12,
  },
  {
    target: '[data-tour="header-notifications"]',
    content: '🔔 Notifications - Stay updated with pairing requests, user activity, and important announcements related to your school\'s mentorship program.',
    placement: 'bottom',
    disableBeacon: true,
    offset: 12,
  },
  {
    target: '[data-tour="header-help"]',
    content: '❓ Help & Support - Access help articles, FAQs, and support resources specific to your role as a Site Leader. Learn about best practices for managing school pairings.',
    placement: 'bottom',
    disableBeacon: true,
    offset: 12,
  },
  {
    target: '[data-tour="header-profile"]',
    content: '👤 Profile Settings - Update your profile information, change your password, and manage your account settings.',
    placement: 'bottom',
    disableBeacon: true,
    offset: 12,
  },
  {
    target: 'body',
    content: '✅ You\'re all set! Start managing your school\'s mentorship program effectively. You can download reports, monitor engagement, and support your teachers\' professional growth.',
    placement: 'center',
  },
];

export const TEACHER_TOUR_STEPS = [
  {
    target: 'body',
    content: '🎉 Welcome to Edifying Teachers! Let\'s help you get started with your professional growth journey. This quick tour will show you how to connect with mentors and make the most of your mentorship experience.',
    placement: 'center',
    disableBeacon: true,
  },
  {
    target: '[data-tour="sidebar-dashboard"]',
    content: '📊 Your Dashboard is your home base. See your profile completion percentage, number of completed sessions, active goals set by your edifier, recent Ms & Cs (Moments and Celebrations), and upcoming meetings at a glance.',
    placement: 'right',
    disableBeacon: true,
    offset: 12,
  },
  {
    target: '[data-tour="sidebar-edifiers"]',
    content: '🔍 Find An Edifier - Browse available edifiers (experienced mentors) to find the perfect match for you. View their profiles, specialties, introduction videos, and request to be paired with them based on your professional development needs.',
    placement: 'right',
    disableBeacon: true,
    offset: 12,
  },
  {
    target: '[data-tour="sidebar-pairing"]',
    content: '👥 Your Edifiers - Once paired, this is where you\'ll see all your active mentorship relationships. View your edifier\'s profile, see your session history together, check your goals, and access quick actions like scheduling meetings.',
    placement: 'right',
    disableBeacon: true,
    offset: 12,
  },
  {
    target: '[data-tour="sidebar-invites"]',
    content: '📬 Mentoring Session Invites - When an edifier sends you a meeting invitation, it appears here. Review the proposed date and time, then accept or decline. You\'ll see who sent the invite and any notes they included.',
    placement: 'right',
    disableBeacon: true,
    offset: 12,
  },
  {
    target: '[data-tour="sidebar-sessions"]',
    content: '📅 Upcoming Mentoring Sessions - View all your scheduled mentoring sessions. The "Join Meeting" button appears 10 minutes before each session. After meetings, you\'ll complete a brief survey to reflect on your learning.',
    placement: 'right',
    disableBeacon: true,
    offset: 12,
  },
  {
    target: '[data-tour="sidebar-availability"]',
    content: '🗓️ My Availability - Set your available time slots so edifiers know when you\'re free for meetings. Click time blocks to mark them as available. Your schedule helps coordinate meetings efficiently.',
    placement: 'right',
    disableBeacon: true,
    offset: 12,
  },
  {
    target: '[data-tour="header-notifications"]',
    content: '🔔 Notifications - Stay updated with meeting reminders, new invites, goal updates, and important messages. The red badge shows unread notifications.',
    placement: 'bottom',
    disableBeacon: true,
    offset: 12,
  },
  {
    target: '[data-tour="header-help"]',
    content: '❓ Help Center - Need help? Access FAQs, browse detailed help articles about platform features, and contact support. You can also restart this tour anytime from here!',
    placement: 'bottom',
    disableBeacon: true,
    offset: 12,
  },
  {
    target: '[data-tour="header-profile"]',
    content: '👤 Your Profile - Complete your profile with a photo, professional information, your teaching specialty, your "why" (motivation), key focus areas, and your professional goals. A complete profile helps find the best mentorship match!',
    placement: 'bottom',
    disableBeacon: true,
    offset: 12,
  },
  {
    target: 'body',
    content: '✅ Perfect! You\'re ready to start your mentorship journey. Remember to complete your profile, set your availability, and browse edifiers to find your ideal mentor. Good luck on your professional growth journey!',
    placement: 'center',
  },
];

export const COACH_TOUR_STEPS = [
  {
    target: 'body',
    content: '🎉 Welcome, Edifier! Let\'s explore how you can mentor and guide teachers on this platform. Your experience and wisdom will help teachers grow professionally and make a lasting impact.',
    placement: 'center',
    disableBeacon: true,
  },
  {
    target: '[data-tour="sidebar-dashboard"]',
    content: '📊 Your Dashboard shows your impact at a glance. See the number of completed sessions, list of paired teachers, goals you\'ve set for each mentee, recent surveys from your teachers (Ms & Cs), and your upcoming mentoring schedule.',
    placement: 'right',
    disableBeacon: true,
    offset: 12,
  },
  {
    target: '[data-tour="sidebar-teachers"]',
    content: '👥 Your Mentees - View all teachers you\'re currently mentoring. See their profiles, session history, progress on goals, and access quick actions like scheduling meetings, viewing their availability, or checking their recent surveys.',
    placement: 'right',
    disableBeacon: true,
    offset: 12,
  },
  {
    target: '[data-tour="sidebar-invites"]',
    content: '📬 Mentoring Session Invites - When teachers send you meeting requests, review them here. You\'ll see proposed times, any notes from the teacher, and can accept or decline based on your schedule.',
    placement: 'right',
    disableBeacon: true,
    offset: 12,
  },
  {
    target: '[data-tour="sidebar-sessions"]',
    content: '📅 Upcoming Mentoring Sessions - Your meeting schedule with all paired teachers. Join video calls 10 minutes before each session. During meetings, you\'ll decide whether to enable the AI Note-Taker for automatic transcription and summaries.',
    placement: 'right',
    disableBeacon: true,
    offset: 12,
  },
  {
    target: '[data-tour="sidebar-availability"]',
    content: '🗓️ My Availability - Set your available time slots so teachers know when you\'re free for mentoring. Click time blocks to mark availability. Teachers can only send invites for times you\'ve marked as available.',
    placement: 'right',
    disableBeacon: true,
    offset: 12,
  },
  {
    target: '[data-tour="header-notifications"]',
    content: '🔔 Notifications - Stay informed about meeting invites, survey completions, pairing updates, and important platform announcements. The badge shows unread notification count.',
    placement: 'bottom',
    disableBeacon: true,
    offset: 12,
  },
  {
    target: '[data-tour="header-help"]',
    content: '❓ Help & Resources - Access mentorship best practices, platform guides, FAQs about features like AI Note-Taker, goal setting, and surveys. Contact support anytime you need assistance.',
    placement: 'bottom',
    disableBeacon: true,
    offset: 12,
  },
  {
    target: '[data-tour="header-profile"]',
    content: '👤 Your Profile - Showcase your expertise! Add a professional photo, upload an introduction video (helps teachers get to know you), list your specialties, teaching experience, languages spoken, and share why you mentor. A complete profile attracts the right mentees.',
    placement: 'bottom',
    disableBeacon: true,
    offset: 12,
  },
  {
    target: 'body',
    content: '✅ Perfect! You\'re ready to make a meaningful difference in teachers\' lives. Remember to set your availability, complete your profile with an intro video, and be ready to set goals and celebrate wins with your mentees. Happy mentoring!',
    placement: 'center',
  },
];

export const PRINCIPAL_TOUR_STEPS = [
  {
    target: 'body',
    content: '🎉 Welcome, Principal! Let\'s show you how to oversee your school\'s mentorship program and support your teachers\' professional development journey.',
    placement: 'center',
    disableBeacon: true,
  },
  {
    target: '[data-tour="sidebar-dashboard"]',
    content: '📊 Your Dashboard provides school-level insights. Monitor overall mentorship program performance including total participants, active pairings, session completion rates, engagement levels, and key performance metrics to assess program effectiveness.',
    placement: 'right',
    disableBeacon: true,
    offset: 12,
  },
  {
    target: '[data-tour="sidebar-teachers"]',
    content: '👥 Users - View all teachers and staff participating in your school\'s mentorship program. See who is paired, their meeting frequency, goal completion, and overall engagement. Identify teachers who might need additional support or encouragement.',
    placement: 'right',
    disableBeacon: true,
    offset: 12,
  },
  {
    target: '[data-tour="sidebar-pairing"]',
    content: '🤝 Pairing Management - See all active mentorship pairs in your school. Monitor pairing health, review session frequency, check goal progress, and ensure productive mentorship relationships. Download reports for review meetings.',
    placement: 'right',
    disableBeacon: true,
    offset: 12,
  },
  {
    target: '[data-tour="header-notifications"]',
    content: '🔔 Notifications - Stay informed about your school\'s program activity. Receive updates about new pairings, program milestones, and important platform announcements.',
    placement: 'bottom',
    disableBeacon: true,
    offset: 12,
  },
  {
    target: '[data-tour="header-help"]',
    content: '❓ Help & Support - Access resources about the mentorship program, best practices for supporting teacher growth, platform guides, and contact information for technical support.',
    placement: 'bottom',
    disableBeacon: true,
    offset: 12,
  },
  {
    target: '[data-tour="header-profile"]',
    content: '👤 Profile Settings - Manage your account information, update your profile, and change your password as needed.',
    placement: 'bottom',
    disableBeacon: true,
    offset: 12,
  },
  {
    target: 'body',
    content: '✅ You\'re all set to support your teachers\' professional development! Use the dashboard to monitor progress, celebrate successes, and identify opportunities to strengthen your school\'s mentorship program.',
    placement: 'center',
  },
];

// Helper function to get tour steps based on user role
export const getTourStepsByRole = (role) => {
  const roleUpper = role?.toUpperCase();
  
  switch (roleUpper) {
    case 'ADMIN':
      return ADMIN_TOUR_STEPS;
    case 'SITE-LEADER':
    case 'SITE_LEADER':
      return SITE_LEADER_TOUR_STEPS;
    case 'TEACHER':
      return TEACHER_TOUR_STEPS;
    case 'COACH':
      return COACH_TOUR_STEPS;
    case 'PRINCIPAL':
      return PRINCIPAL_TOUR_STEPS;
    default:
      return [];
  }
};
